import { AccessDecision, AccessGate } from "src/permissions/access";
import { FlowMobileLockedBehavior } from "src/permissions/access/behaviors/FlowMobileBejavior";
import { selectCurrentCompany } from "src/redux/features/user/selectors/userState.selectors";
import { useAppSelector } from "src/redux/hooks/redux";
import { ContactCenterLoadingBody } from "./ContactCenterLoadingBody";
import { CallWidget } from "./calls/CallWidget";
import { CallHistory } from "./calls/history/CallHistory";
import RecordingSettings from "./sms/components/RecordingSettings";
import SettingViewMobile from "./sms/components/SettingViewMobile";
import VoiceMailSettings from "./sms/components/VoiceMailSettings";
import { VirtualRecepcionistTypes } from "./types";
import { useVirtualAssistant } from "./context/VirtualAssistantContext";
import { useCompanyNumbersMultiQuery } from "./porting-request/hooks/useCompanyNumbersMultiQuery";
import { useCallSettingsQuery } from "./widget/components/ConfigureCallFlow/hooks/useCallFlowQuery";
import { useCompanyNumberQuery } from "./widget/components/NumbersConfiguration/hooks/useCompanyNumberQuery";
import { SmsWidget } from "./sms/SmsWidget";

interface Props {
  type: VirtualRecepcionistTypes;
}

export function VirtualReceptionistDashboard({ type }: Props) {
  const currentCompany = useAppSelector(selectCurrentCompany);
  const companies = useAppSelector((state) => state.userState.companies) || [];

  const companiesIds = companies
    .map((item: { companyId?: string | number; company?: { id?: string | number } }) => String(item.companyId ?? item.company?.id ?? ""))
    .filter((id) => id && !Number.isNaN(Number(id)));

  const { sessionNumber, status } = useVirtualAssistant();

  const { data: settings } = useCallSettingsQuery(currentCompany?.id!);

  const numbersQuery = useCompanyNumberQuery();
  const multiNumbersQuery = useCompanyNumbersMultiQuery(companiesIds);

  const numbersData = numbersQuery.data ?? [];
  const hasFlowMobileNumber = numbersData.length > 0;

  const isNumbersQueryPending = numbersQuery.isPending || (numbersQuery.isFetching && numbersQuery.data === undefined);

  const isMultiNumbersQueryPending = multiNumbersQuery.isPending || (multiNumbersQuery.isFetching && multiNumbersQuery.data === undefined);

  const isWaitingForPhoneSession = hasFlowMobileNumber && !sessionNumber && (status === "initializing" || isMultiNumbersQueryPending);

  const isBootstrapping = isNumbersQueryPending || isWaitingForPhoneSession;

  const flowMobileAccess: AccessDecision = {
    granted: hasFlowMobileNumber,
    reason: "FLOW_MOBILE_REQUIRED",
  };

  if (isBootstrapping) {
    return <ContactCenterLoadingBody message="Loading Contact Center..." />;
  }

  const typeComponentMap: Record<VirtualRecepcionistTypes, JSX.Element> = {
    phone: <CallWidget hasFlowMobileNumber={hasFlowMobileNumber} />,

    history: <CallHistory />,

    contacts: <SmsWidget />,

    "voice-mail": (
      <AccessGate
        strategy={flowMobileAccess}
        behavior={FlowMobileLockedBehavior}
        behaviorConfig={{
          message: "Activate Flow Mobile to use Voice Mail",
        }}
      >
        <VoiceMailSettings settings={settings} />
      </AccessGate>
    ),

    recording: (
      <AccessGate
        strategy={flowMobileAccess}
        behavior={FlowMobileLockedBehavior}
        behaviorConfig={{
          message: "Activate Flow Mobile to use Recording",
        }}
      >
        <RecordingSettings settings={settings} />
      </AccessGate>
    ),

    settings: <SettingViewMobile />,
  };

  return typeComponentMap[type] ?? <ContactCenterLoadingBody />;
}
